import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, firstValueFrom, map, Subject } from 'rxjs';
import { User, UserCreate, UserCreateResponse, UserRaw, UserRawPost, UserRawPostResponse } from '../models/user.model';
import { FeedbackService } from './feedback.service';
import { BackendParams, BackendResponse } from '../types/table.types';
import { PostObjectResponse } from '../models/metadata';
import { FormValidity } from '../admin-panel/user-form/user-form.component';

const DEFAULT_POST_RESPONSE: PostObjectResponse<never> = {
  success: false,
  updatedData: undefined
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = '/user';

  private usersSubject = new BehaviorSubject<UserRaw[]>([]);
  users = this.usersSubject.asObservable();

  private submitUserSubject = new Subject<{ data: UserRaw; valid: FormValidity }>();
  submitUser$ = this.submitUserSubject.asObservable();

  private userDataCache: { data: User; timestamp: number } | null = null;

  constructor(private http: HttpClient, private feedbackService: FeedbackService) {
    // Users will be loaded on-demand when admin panel is accessed
  }

  fetchData(params: BackendParams, searchTerm: string = ''): Observable<BackendResponse<UserRaw>> {
    
    const pageIndex = params.pageIndex ? params.pageIndex : 0;
    const pageSize = params.pageSize ? params.pageSize : 0;

    let paginatedUsers = this.listUsers()
      .pipe(
        map(allUsers => this.filterUsers(allUsers, searchTerm)),
        map(filtered => {
          const sorted = this.sortItem([...filtered], params.sortBy, params.sortDir);
          return { sorted, totalLength: filtered.length };
        }),
        map(({ sorted, totalLength }) => {
            return {
            data: sorted.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
            offset: pageIndex * pageSize,
            length: totalLength
            }
        })
      );
      return paginatedUsers
  }

  private filterUsers(users: UserRaw[], searchTerm: string): UserRaw[] {
    if (!searchTerm || !searchTerm.trim()) {
      return users;
    }
    const term = searchTerm.toLowerCase().trim();
    return users.filter(user =>
      (user.email && user.email.toLowerCase().includes(term)) ||
      (user.unixID && user.unixID.toLowerCase().includes(term))
    );
  }

  private sortItem(items: UserRaw[], sortBy: keyof UserRaw, sortDir: 'asc' | 'desc' | ''): UserRaw [] {
    return items.sort((a, b) => {
      let valueA = a[sortBy];
      let valueB = b[sortBy];

      const valueAType = typeof valueA;
      const valueBType = typeof valueB;

      if (valueAType !== valueBType) {
        if (valueAType === 'number') {
          valueA += '';
        }
        if (valueBType === 'number') {
          valueB += '';
        }
      }

      let comparatorResult = 0;
      if (valueA != null && valueB != null) {
        if (valueA > valueB) {
          comparatorResult = 1;
        } else if (valueA < valueB) {
          comparatorResult = -1;
        }
      } else if (valueA != null) {
        comparatorResult = 1;
      } else if (valueB != null) {
        comparatorResult = -1;
      }

      return comparatorResult * (sortDir == 'asc' ? 1 : -1);
    });
  }


  loadUsers() {
    this.getUserData().then(user => {
      // call the API only if the user has admin role
      if (user.roleID === 3) {
        this.listUsers().subscribe(users => {
          this.usersSubject.next(users);
        });
      }
    }).catch(error => {
      console.error('Error fetching user data:', error);
    });
  }

  listUsers(): Observable<UserRaw[]> {
    return this.http.get<UserRaw[]>(`${this.apiUrl}/all`)
      .pipe(
        catchError(this.feedbackService.handleError<UserRaw[]>('listUsers')),
        map(result => this.parseUsers(result))
      );
  }

  getUser(id: string): Observable<UserRaw> { 
    return this.http.get<UserRaw>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.feedbackService.handleError<UserRaw>(`getUser: ${id}`))
      );
  }

  createUser(user: UserCreate): Observable<UserCreateResponse> {
    const responseWrapperSubject: Subject<UserCreateResponse> = new Subject()
    const observable = this.http.post<UserCreate>(`${this.apiUrl}`, user)
      .pipe(
        catchError(error => {
          this.feedbackService.handleError<UserCreate>(`createUser: ${user.email}`)(error);
          throw error
        })
      );

    observable.subscribe({
      next: () => {
        this.feedbackService.emitSuccess(`Successfully added new user ${user.email}`);
        responseWrapperSubject.next({ success: true, updatedData: user })
        this.loadUsers();
      },
      error: () => {
        this.feedbackService.emitError(`Unable to create user with email ${user.email}. Please try again later.`)
        responseWrapperSubject.next({ success: false, updatedData: undefined })
      }
    })
    return responseWrapperSubject.asObservable();
  }

  updateUser(user: UserRawPost) {
    const responseWrapperSubject: Subject<UserRawPostResponse> = new Subject();
    const observable = this.http.put<void>(`${this.apiUrl}/${user.unixID}`, user)
      .pipe(
        catchError(error => {
          this.feedbackService.handleError(`updateUser: ${user.email}`, DEFAULT_POST_RESPONSE)(error);
          throw error; 
        })
      );

    observable.subscribe({
      next: () => {
        this.feedbackService.emitSuccess("Changes were successfully saved");
        responseWrapperSubject.next({ success: true, updatedData: undefined });
        this.loadUsers();
      },
      error: () => {
        this.feedbackService.emitError("Unable to save changes. Please try again after refreshing the page.");
        responseWrapperSubject.next({ success: false, updatedData: undefined });
      }
    });

    return responseWrapperSubject.asObservable();
  }

  deleteUser(user: UserRaw): Observable<boolean> {
    const responseWrapperSubject: Subject<boolean> = new Subject();
    const observable = this.http.delete<boolean>(`${this.apiUrl}/${user.unixID}`)
      .pipe(
        catchError(this.feedbackService.handleError<boolean>('deleteUser', false))
      );

    observable.subscribe(res => {
      if (res) {
        this.feedbackService.emitSuccess(`${user.email} successfully deleted from Planwise users list.`);
        this.loadUsers()
      } else {
        this.feedbackService.emitError(`Unable to delete user ${user.email} Please try again after refreshing the page.`);
      }
      responseWrapperSubject.next(res);
    });

    return responseWrapperSubject.asObservable();
  }

  getUserData(): Promise<User> {
    const now = Date.now();

    // Check if cache is valid
    if (
      this.userDataCache &&
      now - this.userDataCache.timestamp < 8 * 60 * 60 * 1000 && // Cache is less than 8 hours old
      this.userDataCache?.data?.roleID // Ensure role is not empty
    ) {
      return Promise.resolve(this.userDataCache.data);
    }

    // Fetch new data if cache is invalid
    return firstValueFrom(this.http.get<User>('/me'))
      .then(response => {
        if (!response || !response.roleID) {
          throw new Error('Invalid user data received');
        }
        this.userDataCache = {
          data: {
            ...response,
            isViewer: response.roleID === 1
          },
          timestamp: now
        };
        return this.userDataCache.data;
      })
      .catch(error => {
        console.error('An error occurred while fetching user data:', error);
        this.userDataCache = null; // Invalidate cache on error
        throw error;
      });
  }

  private parseUsers(response: UserRaw[]): UserRaw[] {
    return response.map(user => Object.assign(new UserRaw(), user));
  }

  notifySubmitUser(event: { data: UserRaw; valid: FormValidity }) {
    this.submitUserSubject.next(event);
  }

  logout(): Promise<void> {
    return firstValueFrom(this.http.get('/logout', {})).then(() => {
      // Optionally clear any client-side user data or cache
      this.userDataCache = null;
    });
  }
}
